import React, { useState, useEffect } from 'react';
import { Shield, Check, AlertTriangle, Smartphone, MessageSquare, ChevronUp, ChevronDown, Plus, Download } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';

// =============================================================================
// COUNTRY CODES - Easy to update
// =============================================================================
const COUNTRY_CODES = [
  { code: 'US', name: 'United States', dial: '+1' },
  { code: 'CA', name: 'Canada', dial: '+1' },
  { code: 'GB', name: 'United Kingdom', dial: '+44' },
  { code: 'AU', name: 'Australia', dial: '+61' },
  { code: 'DE', name: 'Germany', dial: '+49' },
  { code: 'FR', name: 'France', dial: '+33' },
  { code: 'ES', name: 'Spain', dial: '+34' },
  { code: 'IT', name: 'Italy', dial: '+39' },
  { code: 'NL', name: 'Netherlands', dial: '+31' },
  { code: 'IN', name: 'India', dial: '+91' },
  { code: 'CN', name: 'China', dial: '+86' },
  { code: 'JP', name: 'Japan', dial: '+81' },
  { code: 'KR', name: 'South Korea', dial: '+82' },
];

// =============================================================================
// TYPES
// =============================================================================
interface TwoFactorSettingsProps {
  onUpdate?: () => void;
}

interface TwoFactorStatus {
  enabled: boolean;
  method: 'app' | 'sms' | 'both';
  backupCodesRemaining: number;
  phoneNumber: string | null;
  phoneVerified: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================
const TwoFactorSettings: React.FC<TwoFactorSettingsProps> = ({ onUpdate }) => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  
  // Setup state
  const [showSetup, setShowSetup] = useState(false);
  const [setupMethod, setSetupMethod] = useState<'app' | 'sms'>('app');
  
  // TOTP setup
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  
  // SMS setup
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('US');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  
  // Verification
  const [verifyCode, setVerifyCode] = useState('');
  
  // Backup codes
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  
  // Regenerate backup codes
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [regeneratePassword, setRegeneratePassword] = useState('');
  
  // Disable
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableMethod, setDisableMethod] = useState<'all' | 'app' | 'sms'>('all');

// Trusted devices
  const [trustedDevices, setTrustedDevices] = useState<any[]>([]);
  const [showTrustedDevices, setShowTrustedDevices] = useState(false);

  // Phone change
  const [showChangePhone, setShowChangePhone] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [newCountryCode, setNewCountryCode] = useState('US');
  const [newCountryDropdownOpen, setNewCountryDropdownOpen] = useState(false);
  const [phoneVerifyCode, setPhoneVerifyCode] = useState('');
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);

  // Hover states
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  // Focus states
  const [focusedInput, setFocusedInput] = useState<string | null>(null);

useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    if (showTrustedDevices) {
      loadTrustedDevices();
    }
  }, [showTrustedDevices]);

const loadStatus = async () => {
    try {
      const response = await apiService.get('/profile/2fa/status');
      setStatus(response.data);
    } catch (err) {
      console.error('Failed to load 2FA status:', err);
    }
  };

  const loadTrustedDevices = async () => {
    try {
      const response = await apiService.get('/profile/2fa/trusted-devices');
      setTrustedDevices(response.data.devices);
    } catch (err) {
      console.error('Failed to load trusted devices:', err);
    }
  };

  const handleRevokeTrustedDevice = async (deviceId: number) => {
    try {
      await apiService.delete(`/profile/2fa/trusted-devices/${deviceId}`);
      showSuccess('Device removed');
      await loadTrustedDevices();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to remove device');
    }
  };

  const handleRevokeAllTrustedDevices = async () => {
    try {
      await apiService.delete('/profile/2fa/trusted-devices');
      showSuccess('All trusted devices removed');
      setTrustedDevices([]);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to remove devices');
    }
  };

  const handleSetupStart = async (method: 'app' | 'sms') => {
    setSetupMethod(method);
    setShowSetup(true);
    setVerifyCode('');
    setSmsSent(false);
    
    if (method === 'app') {
      setLoading(true);
      try {
        const response = await apiService.post('/profile/2fa/setup/totp');
        setQrCode(response.data.qrCode);
        setSecret(response.data.secret);
      } catch (err: any) {
        showError(err.response?.data?.error || 'Setup failed');
        setShowSetup(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSendSMS = async () => {
    setLoading(true);
    try {
      const fullPhone = `${COUNTRY_CODES.find(c => c.code === countryCode)?.dial}${phoneNumber}`;
      await apiService.post('/profile/2fa/send-sms', { phoneNumber: fullPhone });
      showSuccess('SMS code sent! Check your phone.');
      setSmsSent(true);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    try {
      const endpoint = setupMethod === 'app' ? '/profile/2fa/enable/totp' : '/profile/2fa/enable/sms';
      const payload: any = { code: verifyCode };
      if (setupMethod === 'sms') {
        const fullPhone = `${COUNTRY_CODES.find(c => c.code === countryCode)?.dial}${phoneNumber}`;
        payload.phoneNumber = fullPhone;
      }
      const response = await apiService.post(endpoint, payload);
      
      setBackupCodes(response.data.backupCodes);
      setShowBackupCodes(true);
      setShowSetup(false);
      setVerifyCode('');
      setPhoneNumber('');
      setSmsSent(false);
      setQrCode('');
      setSecret('');
      showSuccess(`2FA enabled successfully with ${setupMethod === 'app' ? 'authenticator app' : 'SMS'}!`);
      
      await loadStatus();
      onUpdate?.();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendPhoneCode = async () => {
    setLoading(true);
    try {
      const fullPhone = `${COUNTRY_CODES.find(c => c.code === newCountryCode)?.dial}${newPhoneNumber}`;
      await apiService.post('/profile/2fa/send-phone-verification', { phoneNumber: fullPhone });
      showSuccess('Verification code sent to new number!');
      setPhoneCodeSent(true);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyNewPhone = async () => {
    setLoading(true);
    try {
      const fullPhone = `${COUNTRY_CODES.find(c => c.code === newCountryCode)?.dial}${newPhoneNumber}`;
      await apiService.post('/profile/2fa/change-phone', { 
        phoneNumber: fullPhone,
        code: phoneVerifyCode 
      });
      
      showSuccess('Phone number updated successfully!');
      setShowChangePhone(false);
      setNewPhoneNumber('');
      setPhoneVerifyCode('');
      setPhoneCodeSent(false);
      await loadStatus();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await apiService.post('/profile/2fa/disable', { 
        password: disablePassword,
        method: disableMethod === 'all' ? undefined : disableMethod
      });
      
      if (disableMethod === 'all') {
        showSuccess('2FA disabled completely');
      } else {
        showSuccess(`${disableMethod.toUpperCase()} 2FA disabled`);
      }
      
      setShowDisable(false);
      setDisablePassword('');
      await loadStatus();
      onUpdate?.();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    setLoading(true);
    try {
        const response = await apiService.post('/profile/2fa/backup-codes/regenerate', {
        password: regeneratePassword
      });
      
      setBackupCodes(response.data.backupCodes);
      setShowBackupCodes(true);
      setShowRegenerate(false);
      setRegeneratePassword('');
      showSuccess('Backup codes regenerated successfully!');
      
      await loadStatus();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to regenerate backup codes');
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const text = backupCodes.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
    showSuccess('Backup codes downloaded');
  };

  const cancelSetup = () => {
    setShowSetup(false);
    setVerifyCode('');
    setQrCode('');
    setSecret('');
    setSmsSent(false);
    setPhoneNumber('');
  };

  // =============================================================================
  // STYLES
  // =============================================================================
  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-sm)',
    padding: '24px',
    marginBottom: '20px',
    fontFamily: 'var(--font-sans)',
  };

  const sectionHeaderStyle: React.CSSProperties = {
    fontSize: '0.6875rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--v2-text-muted)',
    margin: '0 0 16px 0',
  };

  const titleRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.0625rem',
    fontWeight: 600,
    color: 'var(--v2-text)',
    margin: 0,
  };

  const statusBadgeStyle = (enabled: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.75rem',
    fontWeight: 600,
    backgroundColor: enabled ? 'var(--v2-success-bg)' : 'var(--v2-warning-bg)',
    color: enabled ? 'var(--v2-success)' : '#92400e',
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--v2-text)',
    marginBottom: '6px',
  };

  const getInputStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.9375rem',
    border: focused ? '1px solid var(--v2-primary)' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--v2-surface)',
    color: 'var(--v2-text)',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxSizing: 'border-box',
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
  });

  const getCodeInputStyle = (focused: boolean): React.CSSProperties => ({
    ...getInputStyle(focused),
    fontFamily: 'var(--font-mono)',
    letterSpacing: '0.15em',
    textAlign: 'center',
    fontSize: '1.0625rem',
  });

  const getPrimaryButtonStyle = (hovered: boolean, disabled: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: disabled ? 'var(--v2-border)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.15s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  });

  const getSecondaryButtonStyle = (hovered: boolean, disabled?: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: disabled ? 'var(--v2-text-muted)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    backgroundColor: hovered && !disabled ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  });

  const getDangerButtonStyle = (hovered: boolean, disabled: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: disabled ? 'var(--v2-border)' : hovered ? '#b91c1c' : 'var(--v2-error)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.15s ease',
  });

  const toggleContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    backgroundColor: 'var(--v2-bg)',
    borderRadius: 'var(--radius-md)',
    marginBottom: '20px',
  };

  const getToggleButtonStyle = (isSelected: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '10px 16px',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: isSelected ? '#ffffff' : 'var(--v2-text-tertiary)',
    backgroundColor: isSelected ? 'var(--v2-primary)' : 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  });

  const alertStyle = (type: 'warning' | 'info' | 'success'): React.CSSProperties => ({
    padding: '12px 16px',
    borderRadius: 'var(--radius-md)',
    borderLeft: `4px solid ${type === 'warning' ? '#d97706' : type === 'success' ? 'var(--v2-success)' : 'var(--v2-primary)'}`,
    backgroundColor: type === 'warning' ? 'var(--v2-warning-bg)' : type === 'success' ? 'var(--v2-success-bg)' : 'var(--v2-info-bg)',
    color: type === 'warning' ? '#92400e' : type === 'success' ? 'var(--v2-success-dark)' : 'var(--v2-primary)',
    fontSize: '0.875rem',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  });

  const infoBoxStyle: React.CSSProperties = {
    padding: '16px',
    backgroundColor: 'var(--v2-surface-alt)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--v2-border)',
  };

  const methodCardStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: active ? 'var(--v2-success-bg)' : 'var(--v2-surface-alt)',
    borderRadius: 'var(--radius-md)',
    border: `1px solid ${active ? '#86efac' : 'var(--v2-border)'}`,
    marginBottom: '8px',
  });

  const methodIconContainerStyle = (active: boolean): React.CSSProperties => ({
    width: '36px',
    height: '36px',
    borderRadius: 'var(--radius-md)',
    backgroundColor: active ? 'rgba(34, 197, 94, 0.2)' : 'var(--v2-surface)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const dropdownContainerStyle: React.CSSProperties = {
    position: 'relative',
  };

  const dropdownButtonStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.9375rem',
    border: focused ? '1px solid var(--v2-primary)' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--v2-surface)',
    color: 'var(--v2-text)',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
  });

  const dropdownListStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    zIndex: 100,
    maxHeight: '200px',
    overflowY: 'auto',
    marginTop: '4px',
    padding: '4px',
  };

  const dropdownItemStyle = (hovered: boolean): React.CSSProperties => ({
    padding: '10px 12px',
    cursor: 'pointer',
    backgroundColor: hovered ? 'var(--v2-surface-hover)' : 'transparent',
    fontSize: '0.875rem',
    color: 'var(--v2-text)',
    borderRadius: 'var(--radius-sm)',
  });

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 1000,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-lg)',
    padding: '32px',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    overflowY: 'auto',
    fontFamily: 'var(--font-sans)',
  };

  const modalTitleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: 'var(--v2-text)',
    margin: '0 0 20px 0',
  };

  const backupCodesGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    padding: '16px',
    backgroundColor: 'var(--v2-surface-alt)',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-mono)',
    fontSize: '0.9375rem',
  };

  const linkButtonStyle = (hovered: boolean): React.CSSProperties => ({
    background: 'none',
    border: 'none',
    color: hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: hovered ? 'underline' : 'none',
    padding: 0,
  });

  // =============================================================================
  // COUNTRY DROPDOWN COMPONENT
  // =============================================================================
  const CountryDropdown: React.FC<{
    value: string;
    onChange: (code: string) => void;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
  }> = ({ value, onChange, isOpen, setIsOpen }) => {
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const selected = COUNTRY_CODES.find(c => c.code === value);

    return (
      <div style={dropdownContainerStyle}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          style={dropdownButtonStyle(isOpen)}
        >
          <span>{selected?.dial} {selected?.name}</span>
          {isOpen ? (
            <ChevronUp size={16} strokeWidth={1.5} color="var(--v2-text-tertiary)" />
          ) : (
            <ChevronDown size={16} strokeWidth={1.5} color="var(--v2-text-tertiary)" />
          )}
        </button>
        {isOpen && (
          <div style={dropdownListStyle}>
            {COUNTRY_CODES.map(country => (
              <div
                key={country.code}
                onClick={() => {
                  onChange(country.code);
                  setIsOpen(false);
                }}
                onMouseEnter={() => setHoveredItem(country.code)}
                onMouseLeave={() => setHoveredItem(null)}
                style={dropdownItemStyle(hoveredItem === country.code)}
              >
                {country.dial} {country.name}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // =============================================================================
  // RENDER
  // =============================================================================
  if (!status) {
    return (
      <div style={cardStyle}>
        <p style={{ color: 'var(--v2-text-tertiary)', margin: 0 }}>Loading 2FA settings...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Main Status Card */}
      <div style={cardStyle}>
        <div style={titleRowStyle}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--v2-primary-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Shield size={20} strokeWidth={1.5} color="var(--v2-primary)" />
          </div>
          <h3 style={titleStyle}>Two-Factor Authentication</h3>
          <span style={statusBadgeStyle(status.enabled)}>
            {status.enabled ? (
              <><Check size={12} strokeWidth={2} /> Enabled</>
            ) : (
              <><AlertTriangle size={12} strokeWidth={2} /> Disabled</>
            )}
          </span>
        </div>

        {status.enabled ? (
          <>
            {/* Active Methods */}
            <div style={{ marginBottom: '24px' }}>
              <p style={sectionHeaderStyle}>Active Methods</p>
              
              {(status.method === 'app' || status.method === 'both') && (
                <div style={methodCardStyle(true)}>
                  <div style={methodIconContainerStyle(true)}>
                    <Smartphone size={18} strokeWidth={1.5} color="var(--v2-success)" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 500, color: 'var(--v2-text)' }}>Authenticator App</p>
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--v2-success)' }}>Active</p>
                  </div>
                </div>
              )}
              
              {(status.method === 'sms' || status.method === 'both') && (
                <div style={methodCardStyle(true)}>
                  <div style={methodIconContainerStyle(true)}>
                    <MessageSquare size={18} strokeWidth={1.5} color="var(--v2-success)" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 500, color: 'var(--v2-text)' }}>SMS</p>
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>{status.phoneNumber}</p>
                  </div>
                  <button
                    onClick={() => setShowChangePhone(true)}
                    onMouseEnter={() => setHoveredButton('changePhone')}
                    onMouseLeave={() => setHoveredButton(null)}
                    style={linkButtonStyle(hoveredButton === 'changePhone')}
                  >
                    Change
                  </button>
                </div>
              )}

              {/* Add method button */}
              {status.method !== 'both' && !showSetup && (
                <button
                  onClick={() => handleSetupStart(status.method === 'app' ? 'sms' : 'app')}
                  onMouseEnter={() => setHoveredButton('addMethod')}
                  onMouseLeave={() => setHoveredButton(null)}
                  style={{ ...getSecondaryButtonStyle(hoveredButton === 'addMethod'), marginTop: '12px' }}
                >
                  <Plus size={16} strokeWidth={1.5} />
                  Add {status.method === 'app' ? 'SMS' : 'Authenticator App'}
                </button>
              )}
            </div>

            {/* Backup Codes */}
            <div style={{ marginBottom: '24px' }}>
              <p style={sectionHeaderStyle}>Backup Codes</p>
              <div style={infoBoxStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: 'var(--v2-text)' }}>
                      {status.backupCodesRemaining}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>codes remaining</p>
                  </div>
                  <button
                    onClick={() => setShowRegenerate(true)}
                    onMouseEnter={() => setHoveredButton('regenerate')}
                    onMouseLeave={() => setHoveredButton(null)}
                    style={getSecondaryButtonStyle(hoveredButton === 'regenerate')}
                  >
                    Regenerate
                  </button>
                </div>
                {status.backupCodesRemaining < 3 && (
                  <div style={{ ...alertStyle('warning'), marginTop: '12px', marginBottom: 0 }}>
                    <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
                    <span>Low backup codes! Consider regenerating soon.</span>
                  </div>
                )}
              </div>
            </div>

{/* Trusted Devices */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <p style={sectionHeaderStyle}>Trusted Devices</p>
                <button
                  onClick={() => setShowTrustedDevices(!showTrustedDevices)}
                  onMouseEnter={() => setHoveredButton('toggleDevices')}
                  onMouseLeave={() => setHoveredButton(null)}
                  style={linkButtonStyle(hoveredButton === 'toggleDevices')}
                >
                  {showTrustedDevices ? 'Hide' : 'Show'}
                </button>
              </div>

              {showTrustedDevices && (
                <div style={infoBoxStyle}>
                  {trustedDevices.length === 0 ? (
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>
                      No trusted devices
                    </p>
                  ) : (
                    <>
                      {trustedDevices.map((device) => (
                        <div key={device.id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 0',
                          borderBottom: '1px solid var(--v2-border)',
                        }}>
                          <div>
                            <p style={{ margin: 0, fontWeight: 500, fontSize: '0.875rem', color: 'var(--v2-text)' }}>
                              {device.device_name}
                            </p>
                            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>
                              {device.ip_address} · Last used {new Date(device.last_used_at).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => handleRevokeTrustedDevice(device.id)}
                            onMouseEnter={() => setHoveredButton(`device-${device.id}`)}
                            onMouseLeave={() => setHoveredButton(null)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: hoveredButton === `device-${device.id}` ? 'var(--v2-error)' : 'var(--v2-text-muted)',
                              fontSize: '0.8125rem',
                              cursor: 'pointer',
                              padding: '4px 8px',
                            }}
                          >
                            Revoke
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={handleRevokeAllTrustedDevices}
                        onMouseEnter={() => setHoveredButton('revokeAll')}
                        onMouseLeave={() => setHoveredButton(null)}
                        style={{ ...getDangerButtonStyle(hoveredButton === 'revokeAll', false), marginTop: '12px', fontSize: '0.8125rem', padding: '8px 14px' }}
                      >
                        Revoke All Devices
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Disable Button */}
            <button
              onClick={() => setShowDisable(true)}
              onMouseEnter={() => setHoveredButton('disable')}
              onMouseLeave={() => setHoveredButton(null)}
              style={getDangerButtonStyle(hoveredButton === 'disable', false)}
            >
              Disable 2FA
            </button>
          </>
        ) : (
          <>
            <p style={{ color: 'var(--v2-text-tertiary)', marginBottom: '24px', lineHeight: 1.6 }}>
              Add an extra layer of security to your account by requiring a verification code when you log in.
            </p>

            {!showSetup ? (
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleSetupStart('app')}
                  onMouseEnter={() => setHoveredButton('setupApp')}
                  onMouseLeave={() => setHoveredButton(null)}
                  style={getPrimaryButtonStyle(hoveredButton === 'setupApp', loading)}
                  disabled={loading}
                >
                  <Smartphone size={18} strokeWidth={1.5} /> Setup Authenticator App
                </button>
                <button
                  onClick={() => { setSetupMethod('sms'); setShowSetup(true); }}
                  onMouseEnter={() => setHoveredButton('setupSms')}
                  onMouseLeave={() => setHoveredButton(null)}
                  style={getSecondaryButtonStyle(hoveredButton === 'setupSms')}
                >
                  <MessageSquare size={18} strokeWidth={1.5} /> Setup SMS
                </button>
              </div>
            ) : null}
          </>
        )}

        {/* Inline Setup Section */}
        {showSetup && (
          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--v2-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                Setup {setupMethod === 'app' ? 'Authenticator App' : 'SMS'} 2FA
              </h4>
              <button
                onClick={cancelSetup}
                onMouseEnter={() => setHoveredButton('cancelSetup')}
                onMouseLeave={() => setHoveredButton(null)}
                style={linkButtonStyle(hoveredButton === 'cancelSetup')}
              >
                Cancel
              </button>
            </div>

            {setupMethod === 'app' && qrCode && (
              <>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <p style={{ color: 'var(--v2-text-tertiary)', marginBottom: '16px' }}>
                    1. Scan this QR code with your authenticator app:
                  </p>
                  <img 
                    src={qrCode} 
                    alt="QR Code" 
                    style={{ width: '180px', height: '180px', borderRadius: 'var(--radius-md)', border: '1px solid var(--v2-border)' }} 
                  />
                  <p style={{ color: 'var(--v2-text-muted)', fontSize: '0.8125rem', marginTop: '12px', fontFamily: 'var(--font-mono)' }}>
                    Or enter manually: {secret}
                  </p>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>2. Enter the 6-digit code from your app:</label>
                  <input
                    type="text"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                    onFocus={() => setFocusedInput('verifyCode')}
                    onBlur={() => setFocusedInput(null)}
                    maxLength={6}
                    placeholder="000000"
                    style={getCodeInputStyle(focusedInput === 'verifyCode')}
                  />
                </div>
              </>
            )}

            {setupMethod === 'sms' && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Phone Number</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ width: '180px' }}>
                      <CountryDropdown
                        value={countryCode}
                        onChange={setCountryCode}
                        isOpen={countryDropdownOpen}
                        setIsOpen={setCountryDropdownOpen}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                        onFocus={() => setFocusedInput('phone')}
                        onBlur={() => setFocusedInput(null)}
                        placeholder="1234567890"
                        style={getInputStyle(focusedInput === 'phone')}
                      />
                    </div>
                  </div>
                  <p style={{ color: 'var(--v2-text-tertiary)', fontSize: '0.8125rem', marginTop: '6px' }}>
                    Enter number without country code
                  </p>
                </div>

                {!smsSent ? (
                  <button
                    onClick={handleSendSMS}
                    onMouseEnter={() => setHoveredButton('sendSms')}
                    onMouseLeave={() => setHoveredButton(null)}
                    style={getPrimaryButtonStyle(hoveredButton === 'sendSms', loading || !phoneNumber)}
                    disabled={loading || !phoneNumber}
                  >
                    Send SMS Code
                  </button>
                ) : (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={alertStyle('success')}>
                      <Check size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
                      <span>Code sent! Check your phone.</span>
                    </div>
                    <label style={labelStyle}>Enter the 6-digit code:</label>
                    <input
                      type="text"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                      onFocus={() => setFocusedInput('smsCode')}
                      onBlur={() => setFocusedInput(null)}
                      maxLength={6}
                      placeholder="000000"
                      autoFocus
                      style={getCodeInputStyle(focusedInput === 'smsCode')}
                    />
                  </div>
                )}
              </>
            )}

            {((setupMethod === 'app' && qrCode) || (setupMethod === 'sms' && smsSent)) && (
              <button
                onClick={handleVerify}
                onMouseEnter={() => setHoveredButton('verify')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{ ...getPrimaryButtonStyle(hoveredButton === 'verify', loading || verifyCode.length < 6), width: '100%', justifyContent: 'center', marginTop: '16px' }}
                disabled={loading || verifyCode.length < 6}
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ========== MODALS ========== */}

      {/* Backup Codes Modal */}
      {showBackupCodes && (
        <div style={overlayStyle}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>Save Your Backup Codes</h2>
            
            <div style={alertStyle('warning')}>
              <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
              <span><strong>Important:</strong> Store these codes securely. Each can only be used once.</span>
            </div>
            
            <div style={backupCodesGridStyle}>
              {backupCodes.map((code, i) => (
                <div key={i} style={{ padding: '8px', backgroundColor: 'var(--v2-surface)', borderRadius: 'var(--radius-sm)', textAlign: 'center' }}>
                  {code}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={downloadBackupCodes}
                onMouseEnter={() => setHoveredButton('download')}
                onMouseLeave={() => setHoveredButton(null)}
                style={getSecondaryButtonStyle(hoveredButton === 'download')}
              >
                <Download size={16} strokeWidth={1.5} /> Download Codes
              </button>
              <button
                onClick={() => setShowBackupCodes(false)}
                onMouseEnter={() => setHoveredButton('savedCodes')}
                onMouseLeave={() => setHoveredButton(null)}
                style={getPrimaryButtonStyle(hoveredButton === 'savedCodes', false)}
              >
                I've Saved These Codes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Backup Codes Modal */}
      {showRegenerate && (
        <div style={overlayStyle} onClick={() => setShowRegenerate(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>Regenerate Backup Codes</h2>
            
            <div style={alertStyle('warning')}>
              <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
              <span>This will invalidate all existing backup codes and generate new ones.</span>
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Confirm with your password</label>
              <input
                type="password"
                value={regeneratePassword}
                onChange={(e) => setRegeneratePassword(e.target.value)}
                onFocus={() => setFocusedInput('regeneratePassword')}
                onBlur={() => setFocusedInput(null)}
                autoFocus
                style={getInputStyle(focusedInput === 'regeneratePassword')}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { setShowRegenerate(false); setRegeneratePassword(''); }}
                onMouseEnter={() => setHoveredButton('cancelRegen')}
                onMouseLeave={() => setHoveredButton(null)}
                style={getSecondaryButtonStyle(hoveredButton === 'cancelRegen')}
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerateBackupCodes}
                onMouseEnter={() => setHoveredButton('confirmRegen')}
                onMouseLeave={() => setHoveredButton(null)}
                style={getPrimaryButtonStyle(hoveredButton === 'confirmRegen', loading || !regeneratePassword)}
                disabled={loading || !regeneratePassword}
              >
                {loading ? 'Regenerating...' : 'Regenerate Codes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable 2FA Modal */}
      {showDisable && (
        <div style={overlayStyle} onClick={() => setShowDisable(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>Disable Two-Factor Authentication</h2>
            
            <div style={alertStyle('warning')}>
              <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
              <span>Disabling 2FA will make your account less secure.</span>
            </div>

            {status?.method === 'both' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>What to disable</label>
                <div style={toggleContainerStyle}>
                  <button
                    style={getToggleButtonStyle(disableMethod === 'app')}
                    onClick={() => setDisableMethod('app')}
                  >
                    App Only
                  </button>
                  <button
                    style={getToggleButtonStyle(disableMethod === 'sms')}
                    onClick={() => setDisableMethod('sms')}
                  >
                    SMS Only
                  </button>
                  <button
                    style={getToggleButtonStyle(disableMethod === 'all')}
                    onClick={() => setDisableMethod('all')}
                  >
                    Both
                  </button>
                </div>
              </div>
            )}
            
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Confirm with your password</label>
              <input
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                onFocus={() => setFocusedInput('disablePassword')}
                onBlur={() => setFocusedInput(null)}
                autoFocus
                style={getInputStyle(focusedInput === 'disablePassword')}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { setShowDisable(false); setDisablePassword(''); }}
                onMouseEnter={() => setHoveredButton('cancelDisable')}
                onMouseLeave={() => setHoveredButton(null)}
                style={getSecondaryButtonStyle(hoveredButton === 'cancelDisable')}
              >
                Cancel
              </button>
              <button
                onClick={handleDisable}
                onMouseEnter={() => setHoveredButton('confirmDisable')}
                onMouseLeave={() => setHoveredButton(null)}
                style={getDangerButtonStyle(hoveredButton === 'confirmDisable', loading || !disablePassword)}
                disabled={loading || !disablePassword}
              >
                {loading ? 'Disabling...' : 'Disable 2FA'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Phone Number Modal */}
      {showChangePhone && (
        <div style={overlayStyle} onClick={() => setShowChangePhone(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>Change Phone Number</h2>
            
            <p style={{ color: 'var(--v2-text-tertiary)', marginBottom: '20px' }}>
              Enter your new phone number and verify it with a code.
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>New Phone Number</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ width: '160px' }}>
                  <CountryDropdown
                    value={newCountryCode}
                    onChange={setNewCountryCode}
                    isOpen={newCountryDropdownOpen}
                    setIsOpen={setNewCountryDropdownOpen}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <input
                    type="tel"
                    value={newPhoneNumber}
                    onChange={(e) => setNewPhoneNumber(e.target.value.replace(/\D/g, ''))}
                    onFocus={() => setFocusedInput('newPhone')}
                    onBlur={() => setFocusedInput(null)}
                    placeholder="1234567890"
                    style={getInputStyle(focusedInput === 'newPhone')}
                  />
                </div>
              </div>
            </div>

            {!phoneCodeSent ? (
              <button
                onClick={handleSendPhoneCode}
                onMouseEnter={() => setHoveredButton('sendPhoneCode')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{ ...getPrimaryButtonStyle(hoveredButton === 'sendPhoneCode', loading || !newPhoneNumber), width: '100%', justifyContent: 'center' }}
                disabled={loading || !newPhoneNumber}
              >
                Send Verification Code
              </button>
            ) : (
              <>
                <div style={alertStyle('success')}>
                  <Check size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
                  <span>Code sent to your new number!</span>
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Verification Code</label>
                  <input
                    type="text"
                    value={phoneVerifyCode}
                    onChange={(e) => setPhoneVerifyCode(e.target.value.replace(/\D/g, ''))}
                    onFocus={() => setFocusedInput('phoneVerifyCode')}
                    onBlur={() => setFocusedInput(null)}
                    maxLength={6}
                    placeholder="000000"
                    autoFocus
                    style={getCodeInputStyle(focusedInput === 'phoneVerifyCode')}
                  />
                </div>

                <button
                  onClick={handleVerifyNewPhone}
                  onMouseEnter={() => setHoveredButton('verifyPhone')}
                  onMouseLeave={() => setHoveredButton(null)}
                  style={{ ...getPrimaryButtonStyle(hoveredButton === 'verifyPhone', loading || phoneVerifyCode.length < 6), width: '100%', justifyContent: 'center' }}
                  disabled={loading || phoneVerifyCode.length < 6}
                >
                  {loading ? 'Verifying...' : 'Verify & Update Phone'}
                </button>
              </>
            )}

            <button
              onClick={() => { 
                setShowChangePhone(false); 
                setNewPhoneNumber(''); 
                setPhoneVerifyCode(''); 
                setPhoneCodeSent(false); 
              }}
              onMouseEnter={() => setHoveredButton('cancelPhone')}
              onMouseLeave={() => setHoveredButton(null)}
              style={{ ...getSecondaryButtonStyle(hoveredButton === 'cancelPhone'), width: '100%', marginTop: '12px' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;
